import { useForm, useSelector } from '@tanstack/react-form'
import type { ClientResponseDto } from '@trellis/api'
import { RichTextEditor } from '@trellis/roots/data'
import { DatePicker, FormFooter, FormSection } from '@trellis/roots/forms'
import { Input } from '@trellis/roots/input'
import { Label } from '@trellis/roots/label'
import { RadioGroup, RadioGroupItem } from '@trellis/roots/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@trellis/roots/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@trellis/roots/tooltip'
import { Typography } from '@trellis/roots/typography'
import {
  CreateInvoiceSchema,
  TAX_TYPE_DEFAULTS,
  UpdateInvoiceSchema,
  type CreateInvoice,
  type CreateRecurringInvoice,
  type DocumentDiscountType,
  type DocumentTaxType,
  type EndCondition,
  type InvoiceTaxType,
  type RecurrenceFrequency,
  type UpdateInvoice,
  type UpdateRecurringInvoice,
  PAYMENT_TERMS_DAY_PRESETS,
} from '@trellis/shared'
import { FileText, Info, Repeat, StickyNote, UserRound } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { ClientCombobox } from '@/components/client-combobox'
import { DiscountSection } from '@/components/discount-section'
import {
  DocumentLineItemsSection,
  type SortableLineItem,
  withId,
} from '@/components/document-line-items-section'
import { InvoiceNumberField } from '@/components/invoice-number-field'
import { RemindersSection } from '@/components/reminders'
import { TermsTemplatePicker } from '@/components/terms-templates'
import { TypeCardRadioGroup } from '@/components/type-card-radio-group'
import { useClientQuery } from '@/hooks'
import { useDocumentTotals } from '@/hooks/useDocumentTotals'
import { useNextInvoiceNumberQuery } from '@/hooks/useInvoices'
import { useLineItemValidation } from '@/hooks/useLineItemValidation'
import {
  useDefaultDueDate,
  useOrganizationToday,
  usePaymentTermsDefault,
  useTaxDefaults,
} from '@/hooks/useOrganization'
import { reportInvalidFields } from '@/lib/analytics'
import { createLineItem } from '@/requests/line-items'

import type { LineItemRowValue } from './LineItemRow'
import type { ScheduleSectionValue } from './ScheduleSection'
import { ScheduleSection } from './ScheduleSection'

export enum InvoiceFormMode {
  CREATE = 'CREATE',
  EDIT = 'EDIT',
  EDIT_RECURRING = 'EDIT_RECURRING',
}

/** Defaults pre-populated when editing an existing recurring invoice. */
export interface RecurringInvoiceDefaults {
  frequency: RecurrenceFrequency
  interval?: number
  daysOfWeek?: number[]
  dayOfMonth?: number
  monthOfYear?: number
  nextRunAt?: string
  endCondition: EndCondition
  currency: string
  taxType: InvoiceTaxType
  /** Fraction (e.g. 0.1 for 10%), matching the stored/response representation. */
  taxRate: number
  netDays: number
  notes?: string
  autoSend: boolean
  remindersEnabled: boolean
  reminderScheduleId: string | null
}

function buildEndCondition(
  type: EndCondition['type'],
  maxRunsInput: string,
  endDateInput: string,
): EndCondition | null {
  if (type === 'never') return { type: 'never' }
  if (type === 'after_n') {
    const maxRuns = parseInt(maxRunsInput)
    if (isNaN(maxRuns) || maxRuns < 1) return null
    return { type: 'after_n', maxRuns }
  }
  if (!endDateInput) return null
  return { type: 'on_date', endDate: endDateInput }
}

function defaultScheduleValue(
  enabled: boolean,
  defaults?: RecurringInvoiceDefaults,
): ScheduleSectionValue {
  const endCondition = defaults?.endCondition ?? { type: 'never' as const }
  return {
    enabled,
    frequency: defaults?.frequency ?? 'MONTHLY',
    interval: defaults?.interval ?? 1,
    daysOfWeek: defaults?.daysOfWeek ?? [],
    dayOfMonth: defaults?.dayOfMonth,
    monthOfYear: defaults?.monthOfYear,
    nextRunAt: defaults?.nextRunAt ? defaults.nextRunAt.substring(0, 10) : '',
    endConditionType: endCondition.type,
    maxRuns: endCondition.type === 'after_n' ? String(endCondition.maxRuns) : '',
    endDate: endCondition.type === 'on_date' ? endCondition.endDate : '',
    autoSend: defaults?.autoSend ?? false,
  }
}

async function saveAdHocRowsToRegistry(
  rows: SortableLineItem[],
): Promise<SortableLineItem[]> {
  return Promise.all(
    rows.map(async (row) => {
      if (row.type !== 'adhoc' || !row.saveToRegistry) return row
      try {
        const result = await createLineItem({
          title: row.title,
          description: row.description ?? undefined,
          unitPrice: row.unitPrice,
          unitType: row.unitType,
          quantity: row.quantity,
          discount: row.discount,
          discountType: row.discountType,
          taxable: row.taxable,
        })
        if (result.error) return row
        const created = result.data
        return {
          _id: row._id,
          type: 'registry' as const,
          lineItemId: created.id,
          title: created.title,
          unitPrice: created.unitPrice,
          unitType: created.unitType,
          description: created.description,
          taxable: created.taxable,
          quantity: row.quantity,
          discountType: row.discountType,
          discount: row.discount,
        } satisfies SortableLineItem
      } catch {
        return row
      }
    }),
  )
}

export interface InvoiceFormSummary {
  selectedClientId: string
  selectedClient: ClientResponseDto | null
  invoiceNumber: string
  subtotal: number
  discountType: DocumentDiscountType
  discount: number
  discountAmount: number
  taxAmount: number
  total: number
  currency: string
  taxType: InvoiceTaxType
  taxRate: number
}

// Currency and locale are fixed at AUD / en-AU for now.
// When multi-currency support is added, restore the form fields.

const INVOICE_TYPE_OPTIONS = [
  {
    value: 'ONE_OFF',
    label: 'One-off invoice',
    description: 'A single invoice for work or items you are billing once.',
    icon: FileText,
  },
  {
    value: 'RECURRING',
    label: 'Recurring invoice',
    description: 'Invoices created automatically on a schedule you set.',
    icon: Repeat,
  },
] as const

interface InvoiceFormProps {
  mode: InvoiceFormMode
  // taxType/taxRate excluded: fixed from org tax settings (create) or the existing
  // document (edit) — see fixedTaxType/fixedTaxRate — never sourced from this prop.
  defaultValues?: Partial<Omit<CreateInvoice, 'taxType' | 'taxRate'>>
  initialLineItems?: LineItemRowValue[]
  onSubmit: (
    data: CreateInvoice | UpdateInvoice | CreateRecurringInvoice | UpdateRecurringInvoice,
    lineItems: LineItemRowValue[],
  ) => void
  isSubmitting?: boolean
  onSummaryChange?: (summary: InvoiceFormSummary) => void
  /** Bubbles the full client object up whenever one is picked or created in the combobox. */
  onClientSelect?: (client: ClientResponseDto) => void
  /** Pre-populated schedule defaults for edit-recurring mode. */
  recurringDefaults?: RecurringInvoiceDefaults
  /** Pre-enables the schedule toggle in create mode (e.g. from ?recurring=true). */
  defaultScheduleEnabled?: boolean
  /** Org's default terms template body — applied once on create if terms is still empty. */
  defaultTermsTemplateBody?: string
  /** The invoice's current tax type/rate in edit mode — fixed, displayed read-only. */
  existingTax?: { taxType: InvoiceTaxType; taxRate: number }
}

export function InvoiceForm({
  mode,
  defaultValues,
  initialLineItems,
  onSubmit,
  isSubmitting,
  onSummaryChange,
  onClientSelect,
  recurringDefaults,
  defaultScheduleEnabled = false,
  defaultTermsTemplateBody,
  existingTax,
}: InvoiceFormProps) {
  const [selectedClient, setSelectedClient] = useState<ClientResponseDto | null>(null)
  const [scheduleValue, setScheduleValue] = useState<ScheduleSectionValue>(() =>
    defaultScheduleValue(
      mode === InvoiceFormMode.EDIT_RECURRING || defaultScheduleEnabled,
      recurringDefaults,
    ),
  )

  const [lineItems, setLineItems] = useState<SortableLineItem[]>(() =>
    (initialLineItems ?? []).map(withId),
  )
  const { errorsByRow: lineItemErrors, validate: validateLineItems } =
    useLineItemValidation()
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false)
  const [invoiceNumberBlocked, setInvoiceNumberBlocked] = useState(false)
  const [netDaysOverride, setNetDaysOverride] = useState<number | null>(null)
  const isNewDocument = mode === InvoiceFormMode.CREATE
  const isRecurring =
    mode === InvoiceFormMode.EDIT_RECURRING ||
    (mode === InvoiceFormMode.CREATE && scheduleValue.enabled)

  const { defaultTaxType, defaultTaxRate } = useTaxDefaults()
  const paymentTermsDays = usePaymentTermsDefault()
  const netDays = netDaysOverride ?? recurringDefaults?.netDays ?? paymentTermsDays
  const netDaysOptions = (PAYMENT_TERMS_DAY_PRESETS as readonly number[]).includes(
    netDays,
  )
    ? PAYMENT_TERMS_DAY_PRESETS
    : [...PAYMENT_TERMS_DAY_PRESETS, netDays].sort((first, second) => first - second)
  const organizationToday = useOrganizationToday()
  const defaultDueDate = useDefaultDueDate(organizationToday)

  /**
   * Tax is fixed, not user-editable on this form: set once from the org's tax settings on
   * create, or carried through unchanged from the existing document on edit — see
   * TaxTab.tsx for the only place tax type/rate can be changed. Both are fractions (e.g.
   * 0.1 for 10%), matching the stored/response representation.
   */
  const fixedTaxType: InvoiceTaxType = isNewDocument
    ? (defaultTaxType ?? 'NONE')
    : mode === InvoiceFormMode.EDIT_RECURRING
      ? (recurringDefaults?.taxType ?? 'NONE')
      : (existingTax?.taxType ?? 'NONE')
  const fixedTaxRate: number = isNewDocument
    ? (defaultTaxRate ?? (defaultTaxType ? TAX_TYPE_DEFAULTS[defaultTaxType] : 0))
    : mode === InvoiceFormMode.EDIT_RECURRING
      ? (recurringDefaults?.taxRate ?? 0)
      : (existingTax?.taxRate ?? 0)

  const form = useForm({
    defaultValues: {
      clientId: defaultValues?.clientId ?? '',
      locale: defaultValues?.locale ?? 'en-AU',
      currency: defaultValues?.currency ?? 'AUD',
      discount: defaultValues?.discount ?? 0,
      discountType: defaultValues?.discountType ?? ('NONE' as DocumentDiscountType),
      notes: defaultValues?.notes ?? '',
      terms: defaultValues?.terms ?? '',
      // Store as YYYY-MM-DD; calendar converts to/from Date on select
      issueDate: defaultValues?.issueDate
        ? defaultValues.issueDate.substring(0, 10)
        : organizationToday,
      dueAt: defaultValues?.dueAt
        ? defaultValues.dueAt.substring(0, 10)
        : isNewDocument
          ? defaultDueDate
          : '',
      invoiceNumber: defaultValues?.invoiceNumber ?? '',
      remindersEnabled:
        defaultValues?.remindersEnabled ?? recurringDefaults?.remindersEnabled ?? true,
      reminderScheduleId: (defaultValues?.reminderScheduleId ??
        recurringDefaults?.reminderScheduleId ??
        null) as string | null,
    },
    onSubmitInvalid: ({ formApi }) =>
      reportInvalidFields(
        isRecurring ? 'recurring_invoice' : 'invoice',
        formApi.state.fieldMeta,
      ),
    onSubmit: async ({ value }) => {
      if (!validateLineItems(lineItems)) return

      const savedLineItems = await saveAdHocRowsToRegistry(lineItems)
      setLineItems(savedLineItems)
      const rawLineItems = savedLineItems.map(({ _id: _, ...lineItem }) => lineItem)

      if (isRecurring) {
        if (!scheduleValue.nextRunAt) {
          toast.error('First run date is required')
          return
        }
        const endCondition = buildEndCondition(
          scheduleValue.endConditionType,
          scheduleValue.maxRuns,
          scheduleValue.endDate,
        )
        if (!endCondition) {
          toast.error(
            scheduleValue.endConditionType === 'after_n'
              ? 'End condition requires a valid number of runs'
              : 'End condition requires an end date',
          )
          return
        }
        const nextRunAt = `${scheduleValue.nextRunAt}T00:00:00.000Z`

        if (mode === InvoiceFormMode.CREATE) {
          const processedLineItems = rawLineItems.map((lineItem) => {
            if (lineItem.type === 'registry') {
              return {
                lineItemId: lineItem.lineItemId,
                quantity: lineItem.quantity,
                discount: isNaN(lineItem.discount) ? undefined : lineItem.discount,
                discountType: lineItem.discountType,
              }
            }
            const {
              type: _type,
              saveToRegistry: _saveToRegistry,
              ...adHocFields
            } = lineItem
            return {
              ...adHocFields,
              description: adHocFields.description ?? undefined,
              discount: isNaN(adHocFields.discount) ? undefined : adHocFields.discount,
            }
          })
          const payload: CreateRecurringInvoice = {
            clientId: value.clientId,
            frequency: scheduleValue.frequency,
            interval: scheduleValue.interval,
            daysOfWeek: scheduleValue.daysOfWeek,
            dayOfMonth: scheduleValue.dayOfMonth,
            monthOfYear: scheduleValue.monthOfYear,
            endCondition,
            nextRunAt,
            currency: value.currency,
            // fixedTaxType always comes from the org's tax settings, which the write path
            // already restricts to DocumentTaxType — see TaxTab.tsx.
            taxType: fixedTaxType as DocumentTaxType,
            taxRate: fixedTaxRate,
            netDays,
            notes: value.notes || null,
            autoSend: scheduleValue.autoSend,
            remindersEnabled: value.remindersEnabled,
            reminderScheduleId: value.reminderScheduleId,
            lineItems: processedLineItems.length > 0 ? processedLineItems : [],
          }
          onSubmit(payload, rawLineItems)
        } else {
          // taxType/taxRate omitted: fixed and not user-editable on this form, so the
          // update never touches them — see fixedTaxType/fixedTaxRate.
          const payload: UpdateRecurringInvoice = {
            frequency: scheduleValue.frequency,
            interval: scheduleValue.interval,
            daysOfWeek: scheduleValue.daysOfWeek,
            dayOfMonth: scheduleValue.dayOfMonth,
            monthOfYear: scheduleValue.monthOfYear,
            endCondition,
            nextRunAt,
            currency: value.currency,
            netDays,
            notes: value.notes || undefined,
            autoSend: scheduleValue.autoSend,
            remindersEnabled: value.remindersEnabled,
            reminderScheduleId: value.reminderScheduleId,
          }
          onSubmit(payload, rawLineItems)
        }
        return
      }

      if (invoiceNumberBlocked) {
        toast.error('Fix the invoice reference before saving')
        return
      }
      const invoiceNumber = value.invoiceNumber || undefined

      try {
        if (mode === InvoiceFormMode.CREATE) {
          const processedLineItems = rawLineItems.map((lineItem) => {
            if (lineItem.type === 'registry') {
              return {
                lineItemId: lineItem.lineItemId,
                quantity: lineItem.quantity,
                discount: isNaN(lineItem.discount) ? undefined : lineItem.discount,
                discountType: lineItem.discountType,
              }
            }
            const {
              type: _type,
              saveToRegistry: _saveToRegistry,
              ...adHocFields
            } = lineItem
            return {
              ...adHocFields,
              description: adHocFields.description ?? undefined,
              discount: isNaN(adHocFields.discount) ? undefined : adHocFields.discount,
            }
          })
          onSubmit(
            CreateInvoiceSchema.parse({
              ...value,
              invoiceNumber,
              // fixedTaxType always comes from the org's tax settings, which the write
              // path already restricts to DocumentTaxType — see TaxTab.tsx.
              taxType: fixedTaxType as DocumentTaxType,
              taxRate: fixedTaxRate,
              discount: isNaN(value.discount) ? 0 : value.discount,
              lineItems: processedLineItems.length > 0 ? processedLineItems : undefined,
            }),
            rawLineItems,
          )
        } else {
          // taxType/taxRate omitted: fixed and not user-editable on this form, so the
          // update never touches them — see fixedTaxType/fixedTaxRate.
          const { clientId, ...editValues } = value
          onSubmit(
            UpdateInvoiceSchema.parse({
              ...editValues,
              ...(clientId ? { clientId } : {}),
              invoiceNumber,
              discount: isNaN(editValues.discount) ? 0 : editValues.discount,
            }),
            rawLineItems,
          )
        }
      } catch (caughtError) {
        const message =
          caughtError instanceof Error ? caughtError.message : 'Please check the form'
        toast.error('Unable to save invoice', { description: message })
      }
    },
  })

  // Read form values reactively so derived state re-renders on field change
  const selectedClientId = useSelector(form.store, (s) => s.values.clientId)
  const currentCurrency = useSelector(form.store, (s) => s.values.currency)
  const currentDiscount = useSelector(form.store, (s) => s.values.discount)
  const currentDiscountType = useSelector(form.store, (s) => s.values.discountType)
  const currentInvoiceNumber = useSelector(form.store, (s) => s.values.invoiceNumber)
  const currentDueAt = useSelector(form.store, (s) => s.values.dueAt)

  const { data: nextInvoiceNumberResult } = useNextInvoiceNumberQuery(isNewDocument)

  const { data: prefilledClientResponse } = useClientQuery(
    selectedClientId,
    isNewDocument && !selectedClient,
  )

  /**
   * Hydrates the sidebar client panel when clientId is prefilled but not chosen via the
   * combobox — e.g. New Invoice from a client's profile. Edit mode already resolves the
   * selected client from its own `useClientsQuery` list fetch, so this stays gated to
   * creation to avoid a redundant `GET /clients/:id`.
   */
  useEffect(() => {
    if (!selectedClient && prefilledClientResponse?.data) {
      setSelectedClient(prefilledClientResponse.data)
    }
  }, [selectedClient, prefilledClientResponse])

  /** Pre-fill terms from the org's default template on new documents only, and only once */
  useEffect(() => {
    if (!isNewDocument || isRecurring || !defaultTermsTemplateBody) return
    if (form.getFieldValue('terms')) return
    form.setFieldValue('terms', defaultTermsTemplateBody)
  }, [isNewDocument, isRecurring, defaultTermsTemplateBody])

  /**
   * Re-checks only rows already flagged invalid, so a fixed field's error clears
   * immediately. Rows added since the last save attempt are untouched and stay unflagged
   * until the next save.
   */
  function handleLineItemsChange(nextLineItems: SortableLineItem[]): void {
    setLineItems(nextLineItems)
    const previouslyInvalidIds = new Set(Object.keys(lineItemErrors))
    if (!hasAttemptedSubmit || previouslyInvalidIds.size === 0) return
    validateLineItems(
      nextLineItems.filter((lineItem) => previouslyInvalidIds.has(lineItem._id)),
    )
  }

  const { subtotal, discountAmount, taxAmount, total } = useDocumentTotals(
    lineItems,
    fixedTaxType,
    fixedTaxRate * 100,
    currentCurrency,
    currentDiscount,
    currentDiscountType,
  )
  const discountExceedsSubtotal =
    currentDiscountType === 'FIXED' &&
    !isNaN(currentDiscount) &&
    currentDiscount > subtotal

  useEffect(() => {
    onSummaryChange?.({
      selectedClientId,
      selectedClient,
      invoiceNumber: currentInvoiceNumber,
      subtotal,
      discountType: currentDiscountType,
      discount: currentDiscount,
      discountAmount,
      taxAmount,
      total,
      currency: currentCurrency,
      taxType: fixedTaxType,
      taxRate: fixedTaxRate,
    })
  }, [
    selectedClientId,
    selectedClient,
    currentInvoiceNumber,
    subtotal,
    currentDiscountType,
    currentDiscount,
    discountAmount,
    taxAmount,
    total,
    currentCurrency,
    fixedTaxType,
    fixedTaxRate,
    onSummaryChange,
  ])

  return (
    <form
      noValidate
      onSubmit={(submitEvent) => {
        submitEvent.preventDefault()
        submitEvent.stopPropagation()
        setHasAttemptedSubmit(true)
        // TanStack skips its onSubmit callback when another field is invalid, so this
        // runs unconditionally to avoid hiding line item errors in that case.
        if (!validateLineItems(lineItems)) {
          toast.error('Fix the highlighted line items before saving')
        }
        void form.handleSubmit()
      }}
      className="space-y-5"
    >
      {isNewDocument && (
        <TypeCardRadioGroup
          id="invoice-type"
          label="Invoice type"
          options={INVOICE_TYPE_OPTIONS}
          value={scheduleValue.enabled ? 'RECURRING' : 'ONE_OFF'}
          onChange={(invoiceType) =>
            setScheduleValue((previous) => ({
              ...previous,
              enabled: invoiceType === 'RECURRING',
            }))
          }
        />
      )}

      {/* Client section */}
      <FormSection
        icon={UserRound}
        title="Invoicing to"
        description="Who's being invoiced?"
      >
        {mode !== InvoiceFormMode.EDIT_RECURRING && (
          <form.Field
            name="clientId"
            validators={{ onSubmit: CreateInvoiceSchema.shape.clientId }}
            children={(field) => (
              <div className="space-y-2">
                <Label htmlFor={field.name}>Client</Label>
                <ClientCombobox
                  id={field.name}
                  value={field.state.value}
                  onChange={(client) => {
                    field.handleChange(client.id)
                    setSelectedClient(client)
                    onClientSelect?.(client)
                  }}
                />
                {field.state.meta.errors.length > 0 ? (
                  <Typography variant="body" color="destructive-text">
                    {field.state.meta.errors[0]?.message}
                  </Typography>
                ) : null}
              </div>
            )}
          />
        )}
      </FormSection>

      {/* Invoice details section */}
      <FormSection
        icon={FileText}
        title="Invoice details"
        description={
          isRecurring
            ? 'Each invoice is issued on its run date.'
            : 'Dates and identifier for this invoice.'
        }
      >
        {isRecurring ? (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="invoiceNumber">Invoice reference #</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="More information"
                    >
                      <Info className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Each invoice this schedule creates is numbered when it runs,
                    continuing your invoice sequence. The number shown is a preview of the
                    next one and may change if other invoices are created before the first
                    run.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              id="invoiceNumber"
              value={
                nextInvoiceNumberResult?.data?.invoiceNumber
                  ? `Next: ${nextInvoiceNumberResult.data.invoiceNumber}`
                  : ''
              }
              placeholder="Assigned on each run"
              disabled
              readOnly
            />
          </div>
        ) : (
          <form.Field name="invoiceNumber">
            {(invoiceNumberField) => (
              <InvoiceNumberField
                id="invoiceNumber"
                value={invoiceNumberField.state.value}
                onChange={invoiceNumberField.handleChange}
                onBlur={invoiceNumberField.handleBlur}
                prefillValue={
                  isNewDocument
                    ? nextInvoiceNumberResult?.data?.invoiceNumber
                    : defaultValues?.invoiceNumber
                }
                originalValue={isNewDocument ? undefined : defaultValues?.invoiceNumber}
                disclaimer={
                  isNewDocument
                    ? 'New invoice references will always continue from the highest reference in use — including any custom reference you set here.'
                    : "Changing this invoice's reference will also update where future invoices continue from."
                }
                onAvailabilityChange={setInvoiceNumberBlocked}
              />
            )}
          </form.Field>
        )}

        {isRecurring ? (
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="netDays">Payment terms</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground"
                      aria-label="More information"
                    >
                      <Info className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    Each invoice this schedule creates is due this many days after its
                    issue date. Defaults to Net {paymentTermsDays} days. Change the
                    default in Payment Options.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Select
              value={String(netDays)}
              onValueChange={(selectedDays) => setNetDaysOverride(Number(selectedDays))}
            >
              <SelectTrigger id="netDays" className="w-[160px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {netDaysOptions.map((days) => (
                  <SelectItem key={days} value={String(days)}>
                    Net {days}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 @md:grid-cols-2">
            <form.Field
              name="issueDate"
              children={(field) => (
                <div className="space-y-2">
                  <Label htmlFor={field.name}>Issue Date</Label>
                  <DatePicker
                    id={field.name}
                    value={field.state.value}
                    onChange={field.handleChange}
                    required
                  />
                </div>
              )}
            />

            <form.Field
              name="dueAt"
              validators={{
                // Recurring invoices compute their due date per run from netDays, so the
                // field is shown for consistency but not enforced while the schedule is on.
                onSubmit: isRecurring ? undefined : CreateInvoiceSchema.shape.dueAt,
              }}
              children={(field) => (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor={field.name}>Due Date</Label>
                    {isNewDocument && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              className="text-muted-foreground hover:text-foreground"
                              aria-label="More information"
                            >
                              <Info className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Defaults to Net {paymentTermsDays} days. Change this in
                            Payment Options.
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <DatePicker
                    id={field.name}
                    value={field.state.value}
                    onChange={field.handleChange}
                    required
                  />
                  {field.state.meta.errors.length > 0 ? (
                    <Typography variant="body" color="destructive-text">
                      {field.state.meta.errors[0]?.message}
                    </Typography>
                  ) : null}
                </div>
              )}
            />
          </div>
        )}
      </FormSection>

      {/* Schedule section — chosen via the invoice type on create, locked on for edit-recurring, hidden for one-off edit */}
      <ScheduleSection
        value={scheduleValue}
        onChange={setScheduleValue}
        entityLabel="invoice"
        locked={mode !== InvoiceFormMode.EDIT}
        hidden={
          mode === InvoiceFormMode.EDIT ||
          (mode === InvoiceFormMode.CREATE && !scheduleValue.enabled)
        }
      >
        <div className="space-y-2">
          <Label>On each run</Label>
          <RadioGroup
            value={scheduleValue.autoSend ? 'auto_send' : 'draft'}
            onValueChange={(nextOption) =>
              setScheduleValue((prev) => ({
                ...prev,
                autoSend: nextOption === 'auto_send',
              }))
            }
            className="flex gap-4"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem value="draft" id="on-each-run-draft" />
              <Label htmlFor="on-each-run-draft" className="text-sm font-normal">
                Save as draft
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="auto_send" id="on-each-run-auto-send" />
              <Label htmlFor="on-each-run-auto-send" className="text-sm font-normal">
                Auto-send
              </Label>
            </div>
          </RadioGroup>
        </div>
      </ScheduleSection>

      {/* Line items section */}
      <DocumentLineItemsSection
        value={lineItems}
        onChange={handleLineItemsChange}
        currency={currentCurrency}
        errors={lineItemErrors}
        isEditMode={!isNewDocument}
      />

      {/* Discount section */}
      <DiscountSection
        discount={
          isRecurring
            ? null
            : {
                discountType: currentDiscountType,
                discount: currentDiscount,
                onChange: (discountType, discount) => {
                  form.setFieldValue('discountType', discountType)
                  form.setFieldValue('discount', discount)
                },
                exceedsSubtotal: discountExceedsSubtotal,
              }
        }
        currency={currentCurrency}
      />

      {/* Notes & terms section */}
      <FormSection
        icon={StickyNote}
        title="Notes & terms"
        collapsible
        defaultOpen={Boolean(defaultValues?.notes || defaultValues?.terms)}
        description="Appear at the foot of the invoice. Visible to the client."
      >
        <form.Field
          name="notes"
          children={(field) => (
            <div className="space-y-2">
              <Label htmlFor={field.name}>Notes</Label>
              <RichTextEditor
                id={field.name}
                value={field.state.value}
                onChange={field.handleChange}
                placeholder="Optional payment terms or remarks"
              />
            </div>
          )}
        />

        {!isRecurring && (
          <form.Field
            name="terms"
            children={(field) => (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor={field.name}>Terms</Label>
                  <TermsTemplatePicker onSelect={field.handleChange} />
                </div>
                <RichTextEditor
                  id={field.name}
                  value={field.state.value}
                  onChange={field.handleChange}
                  placeholder="Optional terms and conditions"
                />
              </div>
            )}
          />
        )}
      </FormSection>

      <form.Field name="remindersEnabled">
        {(field) => (
          <form.Field name="reminderScheduleId">
            {(scheduleField) => (
              <RemindersSection
                checked={field.state.value}
                onCheckedChange={field.handleChange}
                clientId={selectedClientId || null}
                scheduleId={scheduleField.state.value}
                onScheduleChange={scheduleField.handleChange}
                dueDate={currentDueAt || null}
                variant={isRecurring ? 'recurring' : 'invoice'}
              />
            )}
          </form.Field>
        )}
      </form.Field>

      <FormFooter
        statusText={
          mode === InvoiceFormMode.EDIT
            ? 'Changes will update this invoice.'
            : mode === InvoiceFormMode.EDIT_RECURRING
              ? 'Changes apply to future runs only.'
              : 'Nothing saved yet.'
        }
        actions={[
          {
            label: isSubmitting
              ? 'Saving...'
              : mode === InvoiceFormMode.EDIT_RECURRING
                ? 'Save changes'
                : 'Save Invoice',
            type: 'submit',
            disabled: isSubmitting,
          },
        ]}
      />
    </form>
  )
}
